/**
 * Brand Palette Wizard — pure logic (deck `tabs/brand-palette-wizard.html`).
 *
 * The wizard lets a user pick their EXACT brand colors by hex, name the palette,
 * and save it to the Color Library for reuse (concept index.md: "select the hex
 * colors themselves and save it to be reused"). This module holds every pure,
 * side-effect-free operation the modal needs — hex validation/normalization, the
 * stop-list reducers (add / remove / edit), the save-payload builder — so they
 * can be unit-tested without React/DOM.
 *
 * No React, no app-util imports (kept hermetic + jest-friendly).
 */

const HEX_RE = /^#[0-9A-F]{6}$/;
const SHORT_HEX_RE = /^#[0-9A-F]{3}$/;

/** At least one stop; a generous ceiling (wheel palettes can drive many segments). */
export const MIN_STOPS = 1;
export const MAX_STOPS = 12;

/** Deck screen-01 starting palette (the exact five swatches shown). */
export const DEFAULT_PALETTE = [
  "#123051",
  "#9B5AF8",
  "#34D399",
  "#F59E0B",
  "#F472B6",
];

/** On-brand fallbacks used when "Add color" needs a fresh, distinct stop. */
const ADD_CYCLE = [
  "#22D3EE",
  "#6D5EF6",
  "#A78BFA",
  "#F59E0B",
  "#34D399",
  "#F472B6",
  "#123051",
];

let _uid = 0;
/** Small monotonic id — pure enough for list keys; deterministic within a run. */
export const makeId = () => {
  _uid += 1;
  return `bpw-${_uid}`;
};

/**
 * Normalize a raw hex string to canonical `#RRGGBB` (uppercase), expanding
 * 3-digit shorthand. Returns null when the input is not a valid 3/6-digit hex.
 */
export const normalizeHex = (raw) => {
  if (raw === undefined || raw === null) return null;
  let v = String(raw).trim().toUpperCase();
  if (!v.startsWith("#")) v = `#${v}`;
  if (SHORT_HEX_RE.test(v)) {
    v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  }
  return HEX_RE.test(v) ? v : null;
};

/** True when a value is a valid hex (3 or 6 digit). */
export const isValidHex = (raw) => normalizeHex(raw) !== null;

/**
 * Sanitize while the user types: keep a single leading `#`, strip non-hex
 * characters, uppercase, and cap at 6 digits. Never returns null — this is the
 * live-input transform, not the commit-time validator.
 */
export const sanitizeHexInput = (raw) => {
  const digits = String(raw === undefined || raw === null ? "" : raw)
    .toUpperCase()
    .replace(/[^0-9A-F]/g, "")
    .slice(0, 6);
  return `#${digits}`;
};

/** Build a stop record. `hex` is normalized; falls back to ink when invalid. */
export const makeStop = (hex, id) => {
  const norm = normalizeHex(hex) || "#123051";
  return { id: id || makeId(), hex: norm, draft: norm };
};

/** The wizard's initial stop list (deck default palette). */
export const initialStops = () => DEFAULT_PALETTE.map((hex) => makeStop(hex));

/** Pick the next distinct on-brand color not already present in `stops`. */
const nextAddColor = (stops) => {
  const used = new Set(stops.map((s) => s.hex));
  const fresh = ADD_CYCLE.find((c) => !used.has(c));
  return fresh || ADD_CYCLE[stops.length % ADD_CYCLE.length];
};

/** Append a stop (respecting MAX_STOPS). Returns a NEW array. */
export const addStop = (stops, hex) => {
  if (!Array.isArray(stops) || stops.length >= MAX_STOPS) return stops;
  const color = hex ? normalizeHex(hex) || "#123051" : nextAddColor(stops);
  return [...stops, makeStop(color)];
};

/** Remove a stop by id (never below MIN_STOPS). Returns a NEW array. */
export const removeStop = (stops, id) => {
  if (!Array.isArray(stops) || stops.length <= MIN_STOPS) return stops;
  return stops.filter((s) => s.id !== id);
};

/** Commit a normalized hex onto a stop (e.g. from the picker). NEW array. */
export const setStopHex = (stops, id, hex) => {
  const norm = normalizeHex(hex);
  if (!norm) return stops;
  return stops.map((s) => (s.id === id ? { ...s, hex: norm, draft: norm } : s));
};

/**
 * Reflect a live text edit: always store the sanitized draft; when it parses to
 * a valid hex, also advance the committed `hex`. NEW array.
 */
export const setStopDraft = (stops, id, rawDraft) => {
  const draft = sanitizeHexInput(rawDraft);
  const norm = normalizeHex(draft);
  return stops.map((s) =>
    s.id === id ? { ...s, draft, hex: norm || s.hex } : s
  );
};

/** On blur, snap the visible text back to the last committed hex. NEW array. */
export const snapDraft = (stops, id) =>
  stops.map((s) => (s.id === id ? { ...s, draft: s.hex } : s));

/** The committed hex values, in order. */
export const paletteHexes = (stops) =>
  (Array.isArray(stops) ? stops : []).map((s) => s.hex);

/** A palette is saveable with a non-blank name and at least MIN_STOPS valid stops. */
export const isSaveable = ({ name, stops }) => {
  if (!name || !String(name).trim()) return false;
  if (!Array.isArray(stops) || stops.length < MIN_STOPS) return false;
  return stops.every((s) => isValidHex(s.hex));
};

/**
 * Build the savePalette() request body. Matches the shape the existing palette
 * system already POSTs (see ColorLibrary.handleSavePalette / savePalette):
 * type 1 = save-to-library, tab_type 3 = color, usage_type 1 wheel / 2 segment.
 * `id` is intentionally omitted — a wizard palette is always new.
 */
export const buildSavePayload = ({ name, stops, wheelId, usageType = 2 }) => ({
  palette_name: String(name || "Brand colors").trim() || "Brand colors",
  colors: (Array.isArray(stops) ? stops : []).map((s, i) => ({
    hex: s.hex,
    id: s.id,
    selected: false,
    locked: false,
    index: i,
  })),
  wheel_id: wheelId,
  type: 1,
  usage_type: usageType,
  tab_type: 3,
  metadata: { color: stops && stops[0] ? stops[0].hex : undefined },
});

/**
 * Unequal segment spans (degrees, summing to 360) for an N-color preview wheel —
 * the brand-Wheel law forbids equal quarters (concept notes6 #3). Deterministic:
 * a fixed ratio pattern scaled to N colors.
 */
export const spansForCount = (n) => {
  const count = Math.max(1, Math.floor(n) || 1);
  const pattern = [1.32, 1.0, 1.44, 0.88, 1.24, 1.16, 1.08, 0.96];
  const ratios = Array.from({ length: count }, (_, i) => pattern[i % pattern.length]);
  const sum = ratios.reduce((a, b) => a + b, 0);
  return ratios.map((r) => (r * 360) / sum);
};
